import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  Copy,
  Play,
  Tag,
  FileText,
  Search,
  Clock,
  TrendingUp,
  Loader2
} from 'lucide-react';
import { createTestCase, updateTestCase, deleteTestCase } from '@/services/aiTestingService';
import { createComponentLogger } from '@/utils/logger';
import type { TestCase, AgentType } from '@/types/ai-admin';
import { toast } from 'sonner';

const log = createComponentLogger('TestCaseManager', 'ai-testing');

interface TestCaseManagerProps {
  testCases: TestCase[];
  onUpdate: (testCase: TestCase) => void;
  onCreate: (testCase: TestCase) => void;
  onDelete: (id: string) => void;
}

export default function TestCaseManager({ testCases, onUpdate, onCreate, onDelete }: TestCaseManagerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<AgentType | 'all'>('all');
  const [selectedTag, setSelectedTag] = useState<string>('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTestCase, setSelectedTestCase] = useState<TestCase | null>(null);
  const [formData, setFormData] = useState<Partial<TestCase>>({
    name: '',
    description: '',
    query: '',
    expectedKeywords: [],
    agentType: 'general',
    tags: []
  });
  const [keywordInput, setKeywordInput] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const agents: Array<{ value: AgentType | 'all'; label: string }> = [
    { value: 'all', label: 'All Agents' },
    { value: 'encompass_helper', label: 'Encompass Helper' },
    { value: 'pre_approval', label: 'Pre-Approval' },
    { value: 'document_checklist', label: 'Document Checklist' },
    { value: 'mortgage_education', label: 'Mortgage Education' },
    { value: 'investor_guidelines', label: 'Investor Guidelines' },
    { value: 'wisr', label: 'Wisr' },
    { value: 'general', label: 'General' }
  ];

  // Get all unique tags
  const allTags = Array.from(new Set(testCases.flatMap(tc => tc.tags || [])));

  // Filter test cases
  const filteredTestCases = testCases.filter(tc => {
    const matchesSearch = searchQuery === '' ||
      tc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tc.query.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesAgent = selectedAgent === 'all' || tc.agentType === selectedAgent;

    const matchesTag = selectedTag === 'all' ||
      (tc.tags && tc.tags.includes(selectedTag));

    return matchesSearch && matchesAgent && matchesTag;
  });

  // Handle create dialog open
  const handleCreateOpen = () => {
    setFormData({
      name: '',
      description: '',
      query: '',
      expectedKeywords: [],
      agentType: 'general',
      tags: []
    });
    setKeywordInput('');
    setTagInput('');
    setShowCreateDialog(true);
  };

  // Handle edit dialog open
  const handleEditOpen = (testCase: TestCase) => {
    setSelectedTestCase(testCase);
    setFormData({
      name: testCase.name,
      description: testCase.description,
      query: testCase.query,
      expectedKeywords: testCase.expectedKeywords || [],
      agentType: testCase.agentType,
      tags: testCase.tags || []
    });
    setKeywordInput('');
    setTagInput('');
    setShowEditDialog(true);
  };

  // Handle delete dialog open
  const handleDeleteOpen = (testCase: TestCase) => {
    setSelectedTestCase(testCase);
    setShowDeleteDialog(true);
  };

  // Add keyword
  const addKeyword = () => {
    if (keywordInput.trim() && formData.expectedKeywords) {
      setFormData({
        ...formData,
        expectedKeywords: [...formData.expectedKeywords, keywordInput.trim()]
      });
      setKeywordInput('');
    }
  };

  // Remove keyword
  const removeKeyword = (keyword: string) => {
    setFormData({
      ...formData,
      expectedKeywords: formData.expectedKeywords?.filter(k => k !== keyword) || []
    });
  };

  // Add tag
  const addTag = () => {
    if (tagInput.trim() && formData.tags) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  // Remove tag
  const removeTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter(t => t !== tag) || []
    });
  };

  // Handle create submit
  const handleCreateSubmit = async () => {
    if (!formData.name || !formData.query) {
      log.warn('Missing required fields');
      return;
    }

    try {
      setIsSubmitting(true);

      // Call service to create in database
      const newTestCase = await createTestCase({
        name: formData.name,
        description: formData.description || '',
        query: formData.query,
        expectedKeywords: formData.expectedKeywords || [],
        agentType: formData.agentType as AgentType || 'general',
        tags: formData.tags || []
      });

      log.info('Test case created', { id: newTestCase.id });

      // Notify parent to update UI
      onCreate(newTestCase);
      setShowCreateDialog(false);
    } catch (error) {
      log.error('Failed to create test case', {}, error as Error);
      toast.error('Failed to create test case. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit submit
  const handleEditSubmit = async () => {
    if (!selectedTestCase || !formData.name || !formData.query) {
      log.warn('Missing required fields');
      return;
    }

    try {
      setIsSubmitting(true);

      // Call service to update in database
      const updatedTestCase = await updateTestCase(selectedTestCase.id, {
        name: formData.name,
        description: formData.description,
        query: formData.query,
        expectedKeywords: formData.expectedKeywords,
        agentType: formData.agentType as AgentType,
        tags: formData.tags
      });

      log.info('Test case updated', { id: updatedTestCase.id });

      // Notify parent to update UI
      onUpdate(updatedTestCase);
      setShowEditDialog(false);
    } catch (error) {
      log.error('Failed to update test case', {}, error as Error);
      toast.error('Failed to update test case. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    if (!selectedTestCase) {
return;
}

    try {
      setIsSubmitting(true);

      // Call service to delete from database
      await deleteTestCase(selectedTestCase.id);

      log.info('Test case deleted', { id: selectedTestCase.id });

      // Notify parent to update UI
      onDelete(selectedTestCase.id);
      setShowDeleteDialog(false);
    } catch (error) {
      log.error('Failed to delete test case', {}, error as Error);
      toast.error('Failed to delete test case. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Duplicate test case
  const duplicateTestCase = (testCase: TestCase) => {
    const duplicated: TestCase = {
      ...testCase,
      id: `tc_${Date.now()}`,
      name: `${testCase.name} (Copy)`,
      createdAt: new Date().toISOString(),
      lastUsed: null,
      runCount: 0,
      avgScore: null
    };
    onCreate(duplicated);
  };

  return (
    <div className="space-y-4">
      {/* Header and Actions */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Test Case Library</CardTitle>
              <CardDescription>
                Create and manage reusable test cases for AI model validation
              </CardDescription>
            </div>
            <Button onClick={handleCreateOpen}>
              <Plus className="mr-2 h-4 w-4" />
              New Test Case
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid gap-4 md:grid-cols-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search test cases..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Agent Filter */}
            <Select value={selectedAgent} onValueChange={(v) => setSelectedAgent(v as AgentType | 'all')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {agents.map(agent => (
                  <SelectItem key={agent.value} value={agent.value}>
                    {agent.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Tag Filter */}
            <Select value={selectedTag} onValueChange={setSelectedTag}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by tag..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tags</SelectItem>
                {allTags.map(tag => (
                  <SelectItem key={tag} value={tag}>
                    {tag}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Test Cases Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredTestCases.length > 0 ? (
          filteredTestCases.map((testCase) => (
            <Card key={testCase.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-base line-clamp-1">
                    {testCase.name}
                  </CardTitle>
                  <Badge variant="outline" className="text-xs">
                    {testCase.agentType.replace('_', ' ')}
                  </Badge>
                </div>
                <CardDescription className="line-clamp-2 text-xs">
                  {testCase.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Query Preview */}
                <div className="p-2 rounded bg-muted text-sm">
                  <p className="line-clamp-2">{testCase.query}</p>
                </div>

                {/* Expected Keywords */}
                {testCase.expectedKeywords && testCase.expectedKeywords.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Expected Keywords:</p>
                    <div className="flex flex-wrap gap-1">
                      {testCase.expectedKeywords.slice(0, 3).map((keyword, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {keyword}
                        </Badge>
                      ))}
                      {testCase.expectedKeywords.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{testCase.expectedKeywords.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {testCase.tags && testCase.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {testCase.tags.map((tag, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        <Tag className="h-2 w-2 mr-1" />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Play className="h-3 w-3" />
                    <span>{testCase.runCount} runs</span>
                  </div>
                  {testCase.avgScore && (
                    <div className="flex items-center space-x-1">
                      <TrendingUp className="h-3 w-3" />
                      <span>{(testCase.avgScore * 100).toFixed(0)}%</span>
                    </div>
                  )}
                  {testCase.lastUsed && (
                    <div className="flex items-center space-x-1">
                      <Clock className="h-3 w-3" />
                      <span>
                        {new Date(testCase.lastUsed).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex space-x-1 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleEditOpen(testCase)}
                  >
                    <Edit className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => duplicateTestCase(testCase)}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleDeleteOpen(testCase)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="md:col-span-2 lg:col-span-3">
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchQuery || selectedAgent !== 'all' || selectedTag !== 'all'
                  ? 'No test cases found matching your filters'
                  : 'No test cases created yet'}
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={handleCreateOpen}
              >
                <Plus className="mr-2 h-4 w-4" />
                Create First Test Case
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowCreateDialog(false);
          setShowEditDialog(false);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {showCreateDialog ? 'Create Test Case' : 'Edit Test Case'}
            </DialogTitle>
            <DialogDescription>
              {showCreateDialog
                ? 'Create a reusable test case for AI model validation'
                : 'Update the test case details'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Encompass Field Query Test"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this test case validates..."
                rows={2}
              />
            </div>

            {/* Query */}
            <div className="space-y-2">
              <Label htmlFor="query">Query</Label>
              <Textarea
                id="query"
                value={formData.query}
                onChange={(e) => setFormData({ ...formData, query: e.target.value })}
                placeholder="Enter the test query..."
                rows={3}
              />
            </div>

            {/* Agent Type */}
            <div className="space-y-2">
              <Label>Agent Type</Label>
              <Select
                value={formData.agentType}
                onValueChange={(v) => setFormData({ ...formData, agentType: v as AgentType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {agents.filter(a => a.value !== 'all').map(agent => (
                    <SelectItem key={agent.value} value={agent.value}>
                      {agent.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Expected Keywords */}
            <div className="space-y-2">
              <Label>Expected Keywords</Label>
              <div className="flex space-x-2">
                <Input
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  placeholder="Add keyword..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                />
                <Button type="button" variant="outline" onClick={addKeyword}>
                  Add
                </Button>
              </div>
              {formData.expectedKeywords && formData.expectedKeywords.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.expectedKeywords.map((keyword, idx) => (
                    <Badge key={idx} variant="secondary">
                      {keyword}
                      <button
                        className="ml-1 hover:text-destructive"
                        onClick={() => removeKeyword(keyword)}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="space-y-2">
              <Label>Tags</Label>
              <div className="flex space-x-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add tag..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" variant="outline" onClick={addTag}>
                  Add
                </Button>
              </div>
              {formData.tags && formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline">
                      <Tag className="h-2 w-2 mr-1" />
                      {tag}
                      <button
                        className="ml-1 hover:text-destructive"
                        onClick={() => removeTag(tag)}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                setShowEditDialog(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={showCreateDialog ? handleCreateSubmit : handleEditSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {isSubmitting ? 'Saving...' : showCreateDialog ? 'Create' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Test Case?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTestCase?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} disabled={isSubmitting}>
              {isSubmitting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
